import React, { useState, useRef, useEffect } from "react";

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  handleChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  label: string;
  id: string;
  additionalClassName?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  handleChange,
  label,
  id,
  additionalClassName = "",
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Инициализация searchTerm из value
  useEffect(() => {
    if (value) {
      const selectedOption = options.find((o) => o.value === value);
      if (selectedOption) {
        setSearchTerm(selectedOption.label);
      }
    } else {
      setSearchTerm("");
    }
  }, [value, options]);

  // Фильтрация опций
  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Восстанавливаем значение из value при закрытии, если ничего не выбрано
        if (value) {
          const selectedOption = options.find((o) => o.value === value);
          if (selectedOption) {
            setSearchTerm(selectedOption.label);
          }
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [value, options]);

  const handleSelect = (selectedValue: string) => {
    const fakeEvent = {
      target: { value: selectedValue } as HTMLSelectElement,
    } as React.ChangeEvent<HTMLSelectElement>;

    handleChange(fakeEvent);
    setSearchTerm(options.find((o) => o.value === selectedValue)?.label || "");
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-semibold text-gray-700"
        >
          {label}
        </label>
      )}

      <input
        type="text"
        id={id}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setIsOpen(true)}
        placeholder={value ? "" : "Введите для поиска..."}
        className={`text-valley-gray rounded-md py-2 px-3 w-full focus:outline-none ${additionalClassName}`}
      />

      {isOpen && (
        <ul className="absolute w-full bg-white border border-valley-light-gray rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg z-10">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li
                key={option.value}
                className={`px-3 py-2 cursor-pointer hover:bg-valley-background-blue text-valley-gray  ${
                  value === option.value ? "bg-blue-100" : ""
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-gray-500">Нет совпадений</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchableSelect;
